import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
} from "@mui/material";
import { Category as CategoryIcon } from "@mui/icons-material";

interface CustomExpenseTypeDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (customType: string) => void;
}

const CustomExpenseTypeDialog: React.FC<CustomExpenseTypeDialogProps> = ({
  open,
  onClose,
  onConfirm,
}) => {
  const [customType, setCustomType] = useState("");
  const [error, setError] = useState("");

  const handleConfirm = () => {
    if (!customType.trim()) {
      setError("Please enter an expense type");
      return;
    }
    
    if (customType.trim().length < 2) {
      setError("Expense type must be at least 2 characters");
      return;
    }

    if (customType.trim().length > 50) {
      setError("Expense type must not exceed 50 characters");
      return;
    }

    onConfirm(customType.trim());
    handleClose();
  };

  const handleClose = () => {
    setCustomType("");
    setError("");
    onClose();
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleConfirm();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <CategoryIcon color="primary" />
          <Typography variant="h6">
            Enter Expense Type
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Please specify the type of expense for the "Other" category.
        </Typography>
        
        <TextField
          autoFocus
          label="Expense Type"
          fullWidth
          value={customType}
          onChange={(e) => {
            setCustomType(e.target.value);
            if (error) setError("");
          }}
          onKeyPress={handleKeyPress}
          placeholder="e.g., Software Subscription, Training, etc."
          error={!!error}
          helperText={error || "Enter a descriptive name for this expense type"}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button 
          onClick={handleConfirm} 
          variant="contained"
          disabled={!customType.trim()}
        >
          Add Expense
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomExpenseTypeDialog; 